







































































































































import Vue from "vue";
import { User, UserGroup, UserGroupType, UserType } from "@infoland/iprova-api";
import ItemPool, { SearchParams } from "./Functions";
import TeamCard from "./TeamCard.vue";
import { ItemPoolResult, props, UserItem, UserSelectionItem, UserSelectorOptions } from "./types";
import UserCard from "./UserCard.vue";
import UserGroupCard from "./UserGroupCard.vue";
import UserSelectorDialog, { IUserSelectorDialog } from "./UserSelectorDialog.vue";
import { Debouncer } from "@/helpers/newUserSelector";

export default Vue.extend({
    name: "UserSelector",
    components: { UserSelectorDialog, UserCard, UserGroupCard, TeamCard },
    // disable attribute inheritance to be able to forward all attributes to the v- elements
    inheritAttrs: false,
    props: {
        ...props,
        showDialog: { type: Boolean, default: false, required: false },
        /** The value (model) of the user input */
        value: { type: Array as () => UserItem[], default: null, required: false },
        /** Whether the field is required */
        required: { type: Boolean, default: false, required: false }
    },
    data: () => {
        return {
            /** The users retrieved from server for a given search input */
            autoCompleteUsers: [] as UserItem[],
            /** The text being typed in the autocomplete box */
            searchInput: null as string,
            /** The item pool used by the dropdown list to load users and teams */
            pool: null as ItemPool,
            /** The options for the user selector dialog */
            dialogOptions: { allowedTypes: ["user", "team"] } as UserSelectorOptions,
            /** The debounced version for getting items */
            debouncedGetItems: null as Function,
            /** The last search for items that was done using the dropdown */
            lastSearch: undefined as string,
            /** Whether to get search results from the server. This will change to false if a search without name filter returns fewer than the maximum
             * number of items allowed in the dropdown. In this case, any subsequent search would never return more items */
            getResultsFromServer: true as boolean,
            /** Whether the autocomplete is loading data from the server */
            loadingItems: false as boolean,
            /** Whether loading from the server should not be shown in the field */
            loadSilently: false as boolean,
            /** The item to show the card for (as a tooltip-like thing) */
            cardItem: null as UserSelectionItem
        };
    },
    computed: {
        options(): UserSelectorOptions {
            return {
                allowedTypes: this.allowedTypes,
                allowedUserIDs: this.allowedUserIDs,
                allowedUserGroupIDs: this.allowedUserGroupIDs,
                allowedTeamIDs: this.allowedTeamIDs,
                multiple: this.multiple,
                allowedSystemItems: this.allowedSystemItems,
                excludedIDs: this.excludedIDs,
                excludeInactive: false,
                requireEmail: this.requireEmail as "yes" | "no",
                userAccountType: this.userAccountTypeFilter
            };
        },
        /** Local value that has getter and setter. Set emits input event with newValue. Get gets this.value */
        localValue: {
            get(): UserItem[] {
                return this.value;
            },
            set(newValue: UserItem[]) {
                this.$emit("input", newValue);
            }
        },
        /** Gets or sets the value for the autocomplete component */
        autoCompleteValue: {
            get(): UserItem[] | UserItem {
                if (this.value == null) return null;

                if (!this.options.multiple && this.value.length > 0) return this.value[0];

                return this.value;
            },
            set(newValue: UserItem[] | UserItem) {
                if (!newValue) this.localValue = [];
                else if (this.options.multiple) this.localValue = newValue as UserItem[];
                else this.localValue = [newValue as UserItem];
            }
        },
        /** Gets the event listeners for the autocomplete field */
        inputListeners(): Record<string, Function | Function[]> {
            const original: Record<string, Function | Function[]> = this.$listeners;
            delete original["input"];
            return { ...original };
        },
        /** Common properties that are set by default on the v-autocomplete */
        fixedDefaults(): Record<string, any> {
            return {};
        },
        /** Common properties that can be overridden by the user */
        overridableDefaults(): Record<string, any> {
            return {
                color: "primary",
                clearable: !this.options.multiple,
                persistentHint: false
            };
        },
        /** Whether the control should indicate it is loading data from the server */
        loadingFromServer(): boolean {
            return this.loadingItems && !this.loadSilently;
        },
        /** Get autocomplete dropdown loading text */
        loadingText(): string {
            return this.loadingFromServer ? (this.$t("search.loading") as string) : (this.$t("search.noResultsFoundInList") as string);
        },
        /** Whether to show a button to open the dialog */
        showDialogButton(): boolean {
            return this.options.multiple || this.$vuetify.breakpoint.smAndDown || this.showDialog;
        },
        /** Gets whether the item whose card is to be shown is a user */
        cardItemIsUser(): boolean {
            return this.cardItem && this.cardItem.type == "user";
        },
        /** Gets whether the item whose card is to be shown is a user group */
        cardItemIsUserGroup(): boolean {
            return this.cardItem && this.cardItem.type == "usergroup";
        },
        /** Gets whether the item whose card is to be shown is a team */
        cardItemIsTeam(): boolean {
            return this.cardItem && this.cardItem.type == "team";
        }
    },
    async created(): Promise<void> {
        // ensure all already selected people are available in the autocomplete (otherwise they will disappear)
        if (this.value) this.autoCompleteUsers = this.value;
        this.debouncedGetItems = Debouncer.debounce(this.getItemsAsync, 500);

        await this.loadItemDetailsAsync(this.autoCompleteUsers);
    },
    watch: {
        /** Watches for changes in the model */
        value(newValue: UserItem[]): void {
            if (!this.autoCompleteUsers) this.autoCompleteUsers = [];
            newValue.forEach(value => {
                if (!this.autoCompleteUsers.find(x => x.assigneeId == value.assigneeId)) {
                    this.autoCompleteUsers.unshift(value);
                }
            });

            this.loadItemDetailsAsync(this.autoCompleteUsers);
        },
        /** Watches for search input and gets new users and teams based on the input */
        searchInput(): void {
            if (this.getResultsFromServer) {
                // if results need to be loaded from the server, use debounce to reduce load
                this.loadingItems = true;
                this.debouncedGetItems();
            } else {
                // local item search can be immediate
                this.getItemsAsync();
            }
        }
    },
    methods: {
        /** Creates an item pool (makes mocking it easier)
         * @returns a new item pool
         */
        createItemPool(): ItemPool {
            return new ItemPool();
        },
        /** Get the users and teams from the server
         * @returns The users and teams that where requested
         */
        async getItemsAsync(): Promise<void> {
            // do not get items if items were previously gotten with the same search input

            if (this.pool && this.lastSearch === this.searchInput) {
                return;
            }

            const maxItems: number = 100;

            // update last search
            this.lastSearch = this.searchInput;

            // create new item pool for the first search
            if (!this.pool) {
                this.pool = this.createItemPool();
                this.pool.setBatchSize(maxItems);
            }

            if (this.getResultsFromServer) {
                // set search parameters
                const searchParameters: SearchParams = { ...this.options };
                searchParameters.excludeWithoutEmailAddress = this.options.requireEmail == "yes";
                if (this.searchInput) searchParameters.nameLoginCodeEmailContains = this.searchInput;

                this.pool.setSearchParameters(searchParameters);

                // wait for the items to arrive
                this.loadingItems = true;
                const result: ItemPoolResult = await this.pool.getItemsAsync();

                // check if there was a result. If not, the call was cancelled by a subsequent call
                if (result) {
                    // update the autocomplete users
                    this.autoCompleteUsers = result.items.map((i: UserSelectionItem) => {
                        return { assigneeId: i.item_id, name: i.name, type: i.type, data: i.data } as UserItem;
                    });

                    // check whether it is useful to search on the server for subsequent searches
                    if (!this.searchInput && !result.moreItemsAvailable) this.getResultsFromServer = false;

                    // reset item loading and silently flags
                    this.loadingItems = false;
                    this.loadSilently = false;
                }
            }
        },
        /** Loads details for selected items (if they are not yet loaded)
         * @param source The source (and target) for the items to load details
         */
        async loadItemDetailsAsync(source: UserItem[]): Promise<void> {
            if (!source) return;
            const itemsToLoad: UserItem[] = [];
            const itemsIDsToLoad: string[] = [];
            for (let i: number = 0; i < source.length; i++) {
                if (!source[i].data) {
                    itemsToLoad.push(source[i]);
                    itemsIDsToLoad.push(source[i].assigneeId);
                }
            }

            if (itemsToLoad.length > 0) {
                const itemPool: ItemPool = this.createItemPool();
                itemPool.setBatchSize(source.length);
                itemPool.setSearchParameters({
                    allowedTypes: this.options.allowedTypes,
                    allowedSystemItems: this.options.allowedSystemItems,
                    allowedUserIDs: itemsIDsToLoad,
                    allowedUserGroupIDs: itemsIDsToLoad,
                    allowedTeamIDs: itemsIDsToLoad,
                    excludeInactive: false
                });

                // actual call to get items
                const result: ItemPoolResult = await itemPool.getItemsAsync();

                // check first if this queue item was cancelled
                if (result === null) return;

                // rebuild selection (if multiple select)
                for (let i: number = 0; i < result.items.length; i++) {
                    const item: UserSelectionItem = result.items[i];
                    const index: number = source.findIndex(findItem => findItem.assigneeId == item.item_id);
                    if (index > -1) this.$set(source[index], "data", item.data);
                }

                for (let i = 0; i < itemsToLoad.length; i++) {
                    const sourceElement: UserItem = itemsToLoad[i];
                    const index: number = result.items.findIndex(findItem => findItem.item_id == sourceElement.assigneeId);
                    if (index == -1) {
                        //deleted account
                        sourceElement.type = "unknown";
                        sourceElement.name = this.$t("components.fields.users.deletedUser") as string;
                    }
                }
            }
        },
        /** Removes the user with the given id
         * @param id The Id of the user to delete
         */
        removeItem(id: string): void {
            for (let i: number = 0; i < this.localValue.length; i++) {
                if (this.localValue[i].assigneeId == id) {
                    this.localValue.splice(i, 1);
                    break;
                }
            }
        },
        /** Gets the checkbox icon for a multi-select item in the dropdown
         * @param item The item to get the icon for
         * @returns a checked box if the item is selected, otherwise an empty checkbox
         */
        itemCheckbox(item: UserItem): string {
            if (((this.autoCompleteValue || []) as UserItem[]).findIndex(x => x.assigneeId == item.assigneeId) != -1) return "check_box";
            else return "check_box_outline_blank";
        },
        /** Shows the item card for an item (will load data if the data is not yet available)
         * @param item The item to show the card of
         */
        async showItemCardAsync(item: UserItem): Promise<void> {
            // do not show item card if screen is too small
            if (this.$vuetify.breakpoint.smAndDown) return;

            if (!item.data) {
                const source: UserItem[] = [item];
                await this.loadItemDetailsAsync(source);

                // check if the item got its data. If not, don't show the tooltip
                if (!item.data) return;
            }

            const userSelectionItem: UserSelectionItem = { item_id: item.assigneeId, name: item.name, type: item.type, data: item.data };
            // Timeout 0 to make sure you can open a usercard when another one is already open
            setTimeout(() => {
                this.cardItem = userSelectionItem;
            }, 0);
        },
        /** closes any currently showing item card */
        closeItemCard(): void {
            this.cardItem = null;
        },
        /** Gets the icon for a user selection item
         * @param item The item to get the icon for
         * @returns the name of the icon for the item
         */
        getIcon(item: UserItem): string {
            switch (item.type) {
                case "user":
                    return (item.data as any)?.active ? "person" : "no_accounts";
                case "team":
                    return "group_work";
                case "usergroup":
                    return "group";
                case "unknown":
                    return "no_accounts";
                default:
                    return "error";
            }
        },
        /** Gets the color for the item icon
         * @param item the item to get the icon color for
         * @returns the color of the icon, or error if the item is invalid
         */
        getIconColor(item: UserItem): string {
            if (!item.data || item.type == "unknown") return "grey";
            if (item.type == "user") {
                if ((item.data as any).active == false) return "grey";
                if ((item.data as User).user_type === UserType.syncedUser) return "green";
                if ((item.data as User).user_type === UserType.systemUser) return "red";
                return "blue";
            } else if (item.type == "usergroup") {
                if ((item.data as UserGroup).user_group_type === UserGroupType.synced_user_group) return "green";
                if ((item.data as UserGroup).user_group_type === UserGroupType.system_user_group) return "red";
                return "blue";
            } else if (item.type == "team") {
                return "blue";
            } else {
                return "error";
            }
        },
        /** Opens the user selector dialog and updates selected user items  */
        async openUserSelectorAsync(): Promise<void> {
            this.dialogOptions = this.options;
            const selectedItems: UserSelectionItem[] = this.localValue
                ? this.localValue.map(i => {
                      return { item_id: i.assigneeId, name: i.name, type: i.type, data: i.data } as UserSelectionItem;
                  })
                : [];
            const result: UserSelectionItem[] = await (this.$refs.userselector as IUserSelectorDialog).startAsync(selectedItems);
            if (result) {
                this.autoCompleteUsers = result.map(i => {
                    return { assigneeId: i.item_id, name: i.name, type: i.type, data: i.data };
                });
                this.localValue = [...this.autoCompleteUsers];
            }
        }
    }
});
